// Enhanced TutorAgent.jsx with proper session management matching backend
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api, { getCurrentUserId } from "../../services/api";
import Layout from "../../components/Layout/Layout";
import Markdown from "../../components/Markdown/Markdown";
import useLearnerSnapshot from "../../hooks/useLearnerSnapshot";
import useAgentTracker from "../../hooks/useAgentTracker";
import "./tutor.css";

const TutorAgent = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  // Load sidebar state from localStorage, default to true (open)
  const [showSidebar, setShowSidebar] = useState(() => {
    const saved = localStorage.getItem("tutorSidebarOpen");
    return saved !== null ? saved === "true" : true;
  });
  const [conversationSummary, setConversationSummary] = useState(null);
  const [loadingSessions, setLoadingSessions] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const sessionIdRef = useRef(null);
  const initializedRef = useRef(false);

  // What the other agents know about this student (quizzes, courses, earlier questions)
  const { snapshot } = useLearnerSnapshot();

  // Agent tracking
  const { startAgentSession, recordInteraction, endAgentSession } =
    useAgentTracker();

  // Subject and topic from navigation state or defaults
  const query = new URLSearchParams(location.search);
  const subject = location.state?.context?.subject || query.get("subject") || "General";
  const topic = location.state?.context?.topic || query.get("topic") || null;

  // Get user ID consistently with other agents
  const [userId] = useState(() => {
    const userData = JSON.parse(localStorage.getItem("user") || "{}");

    // The signed-in account (from the token) comes first: it is the id the server files conversations under, and
    // it can never be a stale id left in the browser by another account (which the server refuses with a 403).
    const consistentId =
      getCurrentUserId() ||
      userData.user_id ||
      localStorage.getItem("user_id") ||
      userData.student_id ||
      localStorage.getItem("student_id");

    if (consistentId) {
      return consistentId;
    }

    // Generate new ID as last resort
    const newId = `user_${Date.now()}`;
    localStorage.setItem("user_id", newId);
    return newId;
  });

  // Scroll to bottom when new messages are added
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load conversation sessions for sidebar
  const loadSessions = useCallback(async () => {
    try {
      setLoadingSessions(true);
      const response = await api.get(`/api/conversations/sessions/${userId}`);
      setSessions(response.data || []);
    } catch (error) {
      console.error("Failed to load sessions:", error);
    } finally {
      setLoadingSessions(false);
    }
  }, [userId]);

  // Load conversation summary
  const loadSummary = useCallback(async () => {
    try {
      const response = await api.get(`/api/conversations/summary/${userId}`);
      setConversationSummary(response.data);
    } catch (error) {
      console.error("Failed to load summary:", error);
    }
  }, [userId]);

  // Load messages from a specific session
  const loadSession = useCallback(
    async (sessionId) => {
      try {
        setLoading(true);
        const response = await api.get(
          `/api/conversations/sessions/${userId}/${sessionId}/messages`
        );
        const sessionMessages = response.data || [];

        if (sessionMessages.length > 0) {
          const formattedMessages = sessionMessages.flatMap((msg) => [
            {
              type: "user",
              content: msg.question,
              timestamp: msg.timestamp,
            },
            {
              type: "agent",
              content: msg.answer,
              timestamp: msg.timestamp,
            },
          ]);
          setMessages(formattedMessages);
          setCurrentSessionId(sessionId);
          sessionIdRef.current = sessionId;

          // Switch to this session
          await api.post("/api/conversations/sessions/switch", {
            user_id: userId,
            session_id: sessionId,
          });
        } else {
          // Empty session, just set it as current
          setMessages([]);
          setCurrentSessionId(sessionId);
          sessionIdRef.current = sessionId;
        }
        // Don't close sidebar - keep it open for easy navigation
      } catch (error) {
        console.error("Failed to load session:", error);
        setError("Failed to load conversation. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  // Delete a conversation session
  const deleteSession = useCallback(
    async (sessionId, event) => {
      // Stop propagation to prevent loading the session when clicking delete
      if (event) {
        event.stopPropagation();
      }

      const confirmDelete = window.confirm(
        "Are you sure you want to delete this conversation? This cannot be undone."
      );

      if (!confirmDelete) return;

      try {
        setLoading(true);

        // Delete the session via API
        await api.delete(`/api/conversations/sessions/${userId}/${sessionId}`);

        // If the deleted session was the current one, clear messages
        if (currentSessionId === sessionId) {
          setMessages([]);
          setCurrentSessionId(null);
          sessionIdRef.current = null;

          // Show welcome message
          const welcomeMessage = {
            type: "agent",
            content: `Hi there! I'm your AI tutor. What would you like to learn about today?`,
            timestamp: new Date().toISOString(),
          };
          setMessages([welcomeMessage]);
        }

        // Reload sessions list
        await loadSessions();
      } catch (error) {
        console.error("Failed to delete session:", error);
        setError("Failed to delete conversation. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [userId, currentSessionId, loadSessions]
  );

  // Start a fresh conversation. This only creates an empty session on the server (no AI call), so it works
  // instantly and keeps working even when the AI service is slow or unavailable.
  const createNewSession = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setRetryCount(0);

      // The server also closes the previous active session
      const response = await api.post("/api/conversations/sessions/new", {
        user_id: userId,
        title: "New Conversation",
      });
      const newSessionId = response.data?.session_id;
      if (!newSessionId) throw new Error("No session id returned");

      setCurrentSessionId(newSessionId);
      sessionIdRef.current = newSessionId;
      setMessages([
        {
          type: "agent",
          content: "Hi there! I'm your AI tutor. What would you like to learn about today?",
          timestamp: new Date().toISOString(),
        },
      ]);

      // Reload sessions to show the new one
      await loadSessions();
    } catch (error) {
      console.error("Failed to create new session:", error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      setError(
        status === 401
          ? "Your session has expired. Please sign in again."
          : !error?.response
          ? "We couldn't reach the server. Check your internet connection and try again."
          : `Failed to create new conversation${detail && typeof detail === "string" ? `: ${detail}` : ". Please try again."}`
      );
    } finally {
      setLoading(false);
    }
  }, [userId, loadSessions]);

  // Tutor replies are Markdown (bold, lists, tables): render them properly instead of showing the symbols
  const formatResponse = useCallback(
    (text) => (text ? <Markdown className="tutor-md">{text}</Markdown> : text),
    []
  );

  // Initialize agent session and load data
  useEffect(() => {
    // Prevent multiple initializations in strict mode
    if (initializedRef.current) return;
    initializedRef.current = true;

    const initSession = async () => {
      try {
        // Load existing sessions
        await loadSessions();
        await loadSummary();

        // Get or create active session
        const activeSessionResponse = await api.get(
          `/api/conversations/active/${userId}`
        );
        const activeSession = activeSessionResponse.data;

        if (activeSession && activeSession.session_id) {
          const sessionId = activeSession.session_id;
          setCurrentSessionId(sessionId);
          sessionIdRef.current = sessionId;

          // Load messages from active session
          try {
            const messagesResponse = await api.get(
              `/api/conversations/sessions/${userId}/${sessionId}/messages`
            );
            const sessionMessages = messagesResponse.data || [];

            if (sessionMessages.length > 0) {
              const formattedMessages = sessionMessages.flatMap((msg) => [
                {
                  type: "user",
                  content: msg.question,
                  timestamp: msg.timestamp,
                },
                {
                  type: "agent",
                  content: msg.answer,
                  timestamp: msg.timestamp,
                },
              ]);
              setMessages(formattedMessages);
            } else {
              // Empty session - show welcome message
              const welcomeMessage = {
                type: "agent",
                content: `Hi there! I'm your AI tutor. What would you like to learn about today?`,
                timestamp: new Date().toISOString(),
              };
              setMessages([welcomeMessage]);
            }
          } catch (err) {
            console.error("Failed to load session messages:", err);
            // Show welcome message on error
            const welcomeMessage = {
              type: "agent",
              content: `Hi there! I'm your AI tutor. What would you like to learn about today?`,
              timestamp: new Date().toISOString(),
            };
            setMessages([welcomeMessage]);
          }
        } else {
          // No active session - show welcome message
          const welcomeMessage = {
            type: "agent",
            content: `Hi there! I'm your AI tutor, ready to help you learn${
              subject !== "General" ? ` about ${subject}` : ""
            }${
              topic ? `, especially ${topic}` : ""
            }. What's on your mind today?`,
            timestamp: new Date().toISOString(),
          };
          setMessages([welcomeMessage]);
        }

        // Start agent tracking session
        const trackingSessionId = await startAgentSession(
          "tutor",
          subject,
          topic
        );
        console.log("Agent tracking session started:", trackingSessionId);
      } catch (error) {
        console.error("Failed to initialize agent session:", error);
        // Still show welcome message even if initialization fails
        const welcomeMessage = {
          type: "agent",
          content:
            "Hello! I'm your AI tutor and I'm here to help you learn. What would you like to explore today?",
          timestamp: new Date().toISOString(),
        };
        setMessages([welcomeMessage]);
      }
    };

    initSession();

    // Cleanup function
    return () => {
      if (sessionIdRef.current) {
        endAgentSession(sessionIdRef.current);
        console.log("Agent session ended:", sessionIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Persist sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem("tutorSidebarOpen", showSidebar.toString());
  }, [showSidebar]);

  // Enhanced message sending with proper session tracking
  const sendMessage = useCallback(async (suggested) => {
    // A suggestion chip passes its own text; the send button passes a click event
    const text = (typeof suggested === "string" ? suggested : input).trim();
    if (!text) return;

    const userMessage = {
      type: "user",
      content: text,
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, userMessage]);
    const currentInput = text;
    setInput("");
    setLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      // Enhanced request payload matching backend expectations
      const requestPayload = {
        message: currentInput,
        user_id: userId,
        session_id: currentSessionId, // Include current session ID
        subject: subject !== "General" ? subject : undefined,
        topic: topic,
        // Don't include conversation_history - backend will fetch from database
      };

      console.log("Sending request:", {
        message: currentInput.substring(0, 50) + "...",
        session_id: currentSessionId,
        has_session: !!currentSessionId,
      });

      const response = await api.post("/api/agents/tutor/chat", requestPayload);

      if (!response.data || !response.data.response) {
        throw new Error("Invalid response from server");
      }

      // Update session ID if it changed (e.g., new session was created)
      const newSessionId = response.data.conversation_context?.session_id;
      if (newSessionId && newSessionId !== currentSessionId) {
        console.log("Session ID updated:", newSessionId);
        setCurrentSessionId(newSessionId);
        sessionIdRef.current = newSessionId;
      }

      const tutorResponse = {
        type: "agent",
        content: response.data.response,
        mode: response.data.mode || "ai_powered",
        context: response.data.conversation_context,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, tutorResponse]);
      setRetryCount(0); // Reset retry count on success

      // Reload sessions to update sidebar
      await loadSessions();

      // Record the interaction for analytics
      const duration = Date.now() - startTime;
      if (sessionIdRef.current) {
        try {
          await recordInteraction(
            sessionIdRef.current,
            currentInput,
            response.data.response,
            duration
          );
        } catch (trackingError) {
          console.warn("Failed to record interaction:", trackingError);
        }
      }
    } catch (error) {
      console.error("Tutor chat error:", error);

      // Enhanced error handling with retry logic
      let errorMessage = "I'm having trouble connecting right now. ";
      let canRetry = false;

      if (error.response?.status === 503) {
        errorMessage += "The tutoring service is temporarily unavailable.";
        canRetry = retryCount < 2;
      } else if (error.response?.status === 400) {
        errorMessage +=
          "There was an issue with your message. Could you try rephrasing it?";
      } else if (error.response?.status === 429) {
        errorMessage +=
          "I'm getting a lot of requests right now. Please wait a moment before trying again.";
        canRetry = true;
      } else if (error.code === "NETWORK_ERROR" || !error.response) {
        errorMessage += "Please check your internet connection.";
        canRetry = retryCount < 3;
      } else {
        errorMessage += "Please try again in a moment.";
        canRetry = retryCount < 2;
      }

      const errorResponse = {
        type: "agent",
        content: errorMessage,
        isError: true,
        canRetry: canRetry,
        originalMessage: currentInput,
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, errorResponse]);
      setError(errorMessage);

      if (canRetry) {
        setRetryCount((prev) => prev + 1);
      }
    } finally {
      setLoading(false);
    }
  }, [
    input,
    subject,
    topic,
    retryCount,
    recordInteraction,
    userId,
    currentSessionId,
    loadSessions,
  ]);

  // Clear conversation - creates NEW session with force_new_session flag
  const clearConversation = useCallback(async () => {
    if (messages.length > 1) {
      const confirmClear = window.confirm(
        "Are you sure you want to start a new conversation? This will create a fresh session."
      );
      if (!confirmClear) return;
    }

    // Use createNewSession which properly handles force_new_session
    await createNewSession();
  }, [messages.length, createNewSession]);

  // Retry failed message
  const retryMessage = useCallback(
    async (originalMessage) => {
      setInput(originalMessage);
      setTimeout(() => {
        sendMessage();
      }, 100);
    },
    [sendMessage]
  );

  // Handle key press
  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (!loading && input.trim()) {
          sendMessage();
        }
      }
    },
    [sendMessage, loading, input]
  );

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = "auto";
      inputRef.current.style.height =
        Math.min(inputRef.current.scrollHeight, 120) + "px";
    }
  }, [input]);

  return (
    <Layout>
      <div className="agent-container">
        {/* Conversation Sidebar */}
        <div
          className={`conversation-sidebar ${showSidebar ? "open" : "closed"}`}
        >
          <div className="sidebar-header">
            <h3>Conversations</h3>
            <button
              className="close-sidebar-btn"
              onClick={() => setShowSidebar(false)}
              title="Close sidebar"
            >
              ✕
            </button>
          </div>

          <button
            className="new-chat-btn"
            onClick={createNewSession}
            disabled={loading}
          >
            + New Chat
          </button>

          {conversationSummary && (
            <div className="conversation-stats">
              <div className="stat-item">
                <span className="stat-label">Total Chats:</span>
                <span className="stat-value">
                  {conversationSummary.total_sessions}
                </span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Messages:</span>
                <span className="stat-value">
                  {conversationSummary.total_messages}
                </span>
              </div>
            </div>
          )}

          <div className="sessions-list">
            {loadingSessions ? (
              <div className="loading-sessions">Loading sessions...</div>
            ) : sessions.length === 0 ? (
              <div className="no-sessions">No conversations yet</div>
            ) : (
              sessions.map((session) => (
                <div
                  key={session.session_id}
                  className={`session-item ${
                    currentSessionId === session.session_id ? "active" : ""
                  }`}
                  onClick={() => loadSession(session.session_id)}
                >
                  <button
                    className="delete-session-btn"
                    onClick={(e) => deleteSession(session.session_id, e)}
                    title="Delete conversation"
                  >
                    ✕
                  </button>
                  <div className="session-title">
                    {session.title || "Conversation"}
                  </div>
                  <div className="session-preview">
                    {session.last_message
                      ? session.last_message.substring(0, 40) +
                        (session.last_message.length > 40 ? "..." : "")
                      : "New conversation"}
                  </div>
                  <div className="session-meta">
                    <span className="session-date">
                      {new Date(session.updated_at).toLocaleDateString()}
                    </span>
                    <span className="session-count">
                      {session.message_count} msgs
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="agent-header">
          <div className="header-left">
            <button
              className="toggle-sidebar-btn"
              onClick={() => setShowSidebar(!showSidebar)}
              title={
                showSidebar
                  ? "Hide conversation history"
                  : "Show conversation history"
              }
            >
              {showSidebar ? "◄" : "☰"}
            </button>
            <h1 className="agent-title">AI Tutor</h1>
            {showSidebar && (
              <span className="sidebar-hint">💬 Your conversations</span>
            )}
          </div>
          <div className="header-right">
            {(subject !== "General" || topic) && (
              <div className="context-info">
                {subject !== "General" && (
                  <span className="subject-tag">{subject}</span>
                )}
                {topic && <span className="topic-tag">{topic}</span>}
              </div>
            )}
            {messages.length > 1 && (
              <button
                onClick={clearConversation}
                className="clear-chat-btn"
                disabled={loading}
                title="Clear current chat"
              >
                🗑️ Clear Chat
              </button>
            )}
          </div>
        </div>

        <div className="tutor-chat">
          <div className="chat-messages">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`chat-message ${message.type} ${
                  message.isError ? "error-message" : ""
                }`}
              >
                {message.type === "agent" ? (
                  <div className="agent-response">
                    <div className="agent-avatar">
                      {message.isError ? "⚠️" : "🎓"}
                    </div>
                    <div className="agent-content">
                      {formatResponse(
                        index === 0 &&
                          message.type === "agent" &&
                          snapshot?.greeting &&
                          /^Hi there! I'm your AI tutor/.test(message.content || "")
                          ? snapshot.greeting
                          : message.content
                      )}
                      {message.context && (
                        <div className="message-context">
                          {message.context.classification && (
                            <span className="context-tag">
                              {message.context.classification}
                            </span>
                          )}
                          {message.context.current_topic && (
                            <span className="context-tag">
                              📚 {message.context.current_topic}
                            </span>
                          )}
                        </div>
                      )}
                      {message.isError && message.canRetry && (
                        <div className="error-actions">
                          <button
                            onClick={() =>
                              retryMessage(message.originalMessage)
                            }
                            className="retry-button"
                            disabled={loading}
                          >
                            🔄 Retry
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="user-response">
                    <div className="user-content">{message.content}</div>
                    <div className="user-avatar">👤</div>
                  </div>
                )}
              </div>
            ))}

            {messages.length <= 1 && !loading && snapshot && !snapshot.empty && (() => {
              const chips = [];
              (snapshot.studying || []).slice(0, 1).forEach((c) =>
                chips.push(`Help me with “${c.chapter}” in ${c.subject}`));
              (snapshot.focus || []).forEach((f) =>
                chips.push(f.kind === "missed"
                  ? `Explain this to me: ${f.topic}`
                  : `Help me understand ${f.topic} in ${f.subject}`));
              (snapshot.plan || []).slice(0, 1).forEach((pl) =>
                chips.push(`Help me study ${pl.topic || pl.subject} today`));
              const unique = [...new Set(chips)].slice(0, 4);
              if (!unique.length) return null;
              return (
                <div className="tutor-suggest" aria-label="Suggestions based on your learning">
                  <span className="tutor-suggest-label">Pick up where you left off:</span>
                  {unique.map((text) => (
                    <button key={text} type="button" className="tutor-suggest-chip" onClick={() => sendMessage(text)}>
                      {text}
                    </button>
                  ))}
                </div>
              );
            })()}

            {loading && (
              <div className="agent-loading">
                <div className="typing-indicator">
                  <div className="agent-avatar">🎓</div>
                  <div className="typing-content">
                    <span>Your tutor is thinking...</span>
                    <div className="dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="chat-input-container">
            {error && (
              <div className="error-banner">
                <span>{error}</span>
                <button
                  onClick={() => setError(null)}
                  className="dismiss-error"
                >
                  ×
                </button>
              </div>
            )}

            <div className="chat-input">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask me anything about your studies..."
                disabled={loading}
                rows="1"
                className="message-input"
                maxLength={2000}
              />
              <div className="input-actions">
                <button
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className="send-button"
                  title={loading ? "Sending..." : "Send message"}
                >
                  {loading ? "⏳" : "➤"}
                </button>
              </div>
            </div>

            <div className="chat-controls">
              <div className="control-buttons">
                <button
                  onClick={clearConversation}
                  className="clear-button"
                  disabled={loading}
                  title="Start new conversation"
                >
                  ✨ New Conversation
                </button>
              </div>
              <div className="chat-info">
                <span className="tip-text">
                  💡 Tip: Be specific in your questions for better help!
                </span>
                <span className="message-count">
                  {Math.max(0, messages.length - 1)} messages
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="agent-footer">
          <button
            className="back-button"
            onClick={() => navigate("/student-dashboard")}
          >
            ← Back to Dashboard
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default TutorAgent;
